import { FC, useEffect, useRef } from 'react';
import { Stack } from '@mui/material';
import {
  InputCheck,
  InputSelect,
  InputText,
  InputTextArea,
} from '@farmshare/ui-components';
import { useFormikContext } from 'formik';
import { Specification } from '../../types';

interface GlobalSpecQuestionFormProps {
  specifications?: Specification[];
  /** The capability set ID used to group uploaded images in S3. */
  capabilityId: string;
}

/**
 * Form for creating/editing Global Specification questions
 * Used with useModal pattern
 * @param props - Component props
 * @param props.specifications - Available specifications from capabilities
 * @param props.capabilityId - The capability set ID for S3 image grouping
 * @returns Global spec question form component
 */
export const GlobalSpecQuestionForm: FC<GlobalSpecQuestionFormProps> = ({
  specifications = [],
  capabilityId,
}) => {
  const { values, setFieldValue } = useFormikContext<{
    specificationId?: string;
    title?: string;
    description?: string;
    imageUrl?: string;
    [key: string]: unknown;
  }>();

  // When the selected specification changes, default title/description/imageUrl
  // from the spec — but only if the fields are empty, and skip the initial mount
  // so editing an existing question doesn't overwrite the saved values.
  const prevSpecIdRef = useRef<string | undefined>(values.specificationId);
  useEffect(() => {
    const specId = values.specificationId;
    if (!specId || specId === prevSpecIdRef.current) return;
    prevSpecIdRef.current = specId;

    const spec = specifications.find((s) => s.id === specId);
    if (!spec) return;

    setFieldValue('title', spec.title);
    setFieldValue('description', spec.description ?? '');
    setFieldValue('imageUrl', spec.imageUrl ?? '');
  }, [values.specificationId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Any spec can be used as a standalone form question. Whether it's also
  // attached to specific cuts/trims is orthogonal — a spec can live in both
  // places (the standalone answer and any per-item answers are independent).
  const specificationOptions = specifications.map((spec) => ({
    value: spec.id,
    label: `${spec.title} (${spec.inputType})`,
  }));

  return (
    <Stack spacing={3}>
      <InputText label="Question Title" nameOverride="title" required />
      <InputTextArea
        label="Description"
        nameOverride="description"
        rows={2}
        hint="Optional description to help customers understand their options"
      />

      <InputSelect
        label="Specification"
        nameOverride="specificationId"
        options={specificationOptions}
        required
        hint="Pick any specification to ask as a standalone question in this form"
      />

      <InputCheck
        label="Required"
        nameOverride="required"
        hint="Whether customers must answer this question"
      />
    </Stack>
  );
};
